import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const PriceEditContext = createContext({
  editingPriceId: null,
  setEditingPriceId: () => {},
  registerCloseRequest: () => {},
  requestCloseEditing: async () => true,
  editingSnapshotRef: { current: null },
});

export function PriceEditProvider({
  children,
  initialEditingId = null,
  onEditingChange,
  snapshotRef,
}) {
  const [editingPriceId, setEditingPriceIdState] = useState(initialEditingId);

  // Committed snapshot of the price whose form is open (null when none is).
  // A ref, not state: PriceCard writes it synchronously when a card opens or
  // saves, so a product submit fired in the same tick (kebab action →
  // submitForm) reads the fresh snapshot — context state would still be a
  // render behind. ProductDetailsForm passes its own ref down (via PricesList)
  // so submitProduct can substitute the snapshot for the in-flight row.
  const internalSnapshotRef = useRef(null);
  const editingSnapshotRef = snapshotRef || internalSnapshotRef;

  // Wrap the setter so an ancestor (e.g. the create flow's "Create checkout"
  // button) can react to which price is being edited without subscribing to this
  // context itself. Fired from the open/close event handler — no effect needed.
  const setEditingPriceId = useCallback(
    id => {
      setEditingPriceIdState(id);
      onEditingChange?.(id);
    },
    [onEditingChange],
  );

  // The currently-open price card registers an imperative "close me" handler so
  // an ancestor (e.g. "Add price") can collapse it honoring its unsaved-changes
  // prompt. Only one card is open at a time, so a single ref suffices.
  // requestCloseEditing resolves false when the user keeps the open card.
  const closeRequestRef = useRef(null);
  const registerCloseRequest = useCallback(fn => {
    closeRequestRef.current = fn;
  }, []);
  const requestCloseEditing = useCallback(async () => {
    const fn = closeRequestRef.current;
    return fn ? await fn() : { ok: true, revertedPrices: null };
  }, []);

  return (
    <PriceEditContext.Provider
      value={{
        editingPriceId,
        setEditingPriceId,
        registerCloseRequest,
        requestCloseEditing,
        editingSnapshotRef,
      }}
    >
      {children}
    </PriceEditContext.Provider>
  );
}

export function usePriceEdit() {
  return useContext(PriceEditContext);
}
